import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { CONFIG } from "../../common/constant/constant";
import { logout } from "../slice/user.slice";
import { setToast } from "../slice/toast.slice";

export const thirdPartyKeysApi = createApi({
   reducerPath: "thirdPartyKeysApi",
   baseQuery: fetchBaseQuery({
      baseUrl: CONFIG.ADMIN_API_URL,
      // the session is an HttpOnly cookie set by the server, so no token is read or sent from here
      credentials: "include",
   }),
   endpoints: (builder) => ({
      addThirdPartyKeys: builder.mutation({
         query: (payload) => ({
            url: "/third-party-keys/add",
            method: "POST",
            body: payload,
         }),
         transformResponse: (result) => result,
         async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try {
               const { data } = await queryFulfilled;
               if (data?.status) {
                  dispatch(
                     setToast({
                        message: data?.message || "Added successfully",
                        success: true,
                        error: false,
                     })
                  );
               } else {
                  dispatch(
                     setToast({
                        message: data?.message || "Failed to add key",
                        success: false,
                        error: true,
                     })
                  );
               }
            } catch (error) {
               dispatch(
                  setToast({
                     message: error?.error?.data?.message || "server error",
                     success: false,
                     error: true,
                  })
               );
               if (error?.error?.status === 401) {
                  setTimeout(() => dispatch(logout()), 2000);
               }
            }
         },
      }),

      thirdPartyKeysList: builder.mutation({
         query: (payload) => {
            const params = new URLSearchParams({
               provider: payload?.provider ?? "",
               status: payload?.status ?? "",
               search: payload?.search ?? "",
               page: String(payload?.page ?? 1),
               limit: String(payload?.limit ?? 20),
               plan_type: payload?.plan_type ?? "",
               use_for: payload?.use_for ?? "",
            });
            return {
               url: `/third-party-keys/list?${params.toString()}`,
               method: "GET",
            };
         },
         transformResponse: (result) => result,
         async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try {
               await queryFulfilled;
            } catch (error) {
               // Optional: handle auth errors uniformly
               if (error?.error?.status === 401) {
                  setTimeout(() => dispatch(logout()), 2000);
               }
            }
         },
      }),

      thirdPartyKeyInfo: builder.mutation({
         query: (payload) => ({
            url: `/third-party-keys/keyInfo`,
            method: "POST",
            body: payload, // { key, provider }
         }),
         transformResponse: (result) => result,
         async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try {
               await queryFulfilled;
            } catch (error) {
               if (error?.error?.status === 401) {
                  setTimeout(() => dispatch(logout()), 2000);
               }
            }
         },
      }),

      moralisCUUsage: builder.mutation({
         query: (payload) => {
            const params = new URLSearchParams();
            if (payload?.days) params.append("days", String(payload.days));
            const queryString = params.toString();
            return {
               url: `/third-party-keys/moralis-cu-usage${queryString ? `?${queryString}` : ""}`,
               method: "GET",
            };
         },
         transformResponse: (result) => result,
         async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try {
               await queryFulfilled;
            } catch (error) {
               if (error?.error?.status === 401) {
                  setTimeout(() => dispatch(logout()), 2000);
               }
            }
         },
      }),

      thirdPartyKeysStates: builder.mutation({
         query: (payload) => {
            const params = new URLSearchParams();
            if (payload?.source) params.append("source", String(payload.source));
            return {
               url: `/third-party-keys/get-states?${params.toString()}`,
               method: "GET",
            };
         },
         transformResponse: (result) => result,
         async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try {
               await queryFulfilled;
            } catch (error) {
               if (error?.error?.status === 401) {
                  setTimeout(() => dispatch(logout()), 2000);
               }
            }
         },
      }),

      updateThirdPartyKeyStatus: builder.mutation({
         query: (payload) => ({
            url: "/third-party-keys/updateStatus",
            method: "POST",
            body: payload,
         }),
         transformResponse: (result) => result,
         async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try {
               const { data } = await queryFulfilled;
               if (data?.status) {
                  dispatch(
                     setToast({
                        message: data?.message || "Status updated successfully",
                        success: true,
                        error: false,
                     })
                  );
               } else {
                  dispatch(
                     setToast({
                        message: data?.message || "Failed to update status",
                        success: false,
                        error: true,
                     })
                  );
               }
            } catch (error) {
               dispatch(
                  setToast({
                     message: error?.error?.data?.message || "Server error",
                     success: false,
                     error: true,
                  })
               );
               if (error?.error?.status === 401) {
                  setTimeout(() => dispatch(logout()), 2000);
               }
            }
         },
      }),

      updateThirdPartyKeyDetails: builder.mutation({
         query: (payload) => ({
            url: "/third-party-keys/updateDetails",
            method: "POST",
            body: payload,
         }),
         transformResponse: (result) => result,
         async onQueryStarted(args, { dispatch, queryFulfilled }) {
            try {
               const { data } = await queryFulfilled;
               if (data?.status) {
                  dispatch(
                     setToast({
                        message: data?.message || "Details updated successfully",
                        success: true,
                        error: false,
                     })
                  );
               } else {
                  dispatch(
                     setToast({
                        message: data?.message || "Failed to update details",
                        success: false,
                        error: true,
                     })
                  );
               }
            } catch (error) {
               dispatch(
                  setToast({
                     message: error?.error?.data?.message || "Server error",
                     success: false,
                     error: true,
                  })
               );
               if (error?.error?.status === 401) {
                  setTimeout(() => dispatch(logout()), 2000);
               }
            }
         },
      }),
   }),
});

export const { 
   useAddThirdPartyKeysMutation, 
   useThirdPartyKeysListMutation, 
   useThirdPartyKeyInfoMutation,
   useMoralisCUUsageMutation,
   useThirdPartyKeysStatesMutation,
   useUpdateThirdPartyKeyStatusMutation,
   useUpdateThirdPartyKeyDetailsMutation
} = thirdPartyKeysApi;


